/**
 * 工作流执行引擎 V2
 * 支持条件、循环、并行等高级特性
 */

import { EventEmitter } from 'events';
import { 
  WorkflowDefinitionV2, 
  WorkflowNode, 
  WorkflowExecutionContext, 
  WorkflowExecutionResult,
  WorkflowCondition,
  LoopConfig,
  ActionConfig
} from '../types/workflow-v2';
import { ExtendedTask } from '../types/extended-task';
import { TaskStore } from './task-store';
import { SmartAgentAllocator } from './smart-agent-allocator';
import { AgentManager } from './agent-manager';
import { WorkflowMonitor } from './workflow-monitor';
import { WorkflowLogger } from './workflow-logger';
import { logger } from '../utils/logger';
import { exec } from 'child_process';
import { promisify } from 'util';
import axios from 'axios';
import vm from 'vm';

const execAsync = promisify(exec);

export interface WorkflowEngineOptions {
  maxConcurrency?: number;
  enableAI?: boolean;
  userId?: string;
  projectId?: string;
  enableMonitoring?: boolean;
  enableLogging?: boolean;
  monitorOptions?: any;
  loggerOptions?: any;
}

export class WorkflowEngineV2 extends EventEmitter {
  private taskStore: TaskStore;
  private agentManager: AgentManager;
  private agentAllocator?: SmartAgentAllocator;
  private monitor?: WorkflowMonitor;
  private logger?: WorkflowLogger;
  private options: WorkflowEngineOptions;
  private runningWorkflows: Map<string, WorkflowExecutionContext> = new Map();
  
  constructor(options: WorkflowEngineOptions = {}) {
    super();
    this.options = {
      maxConcurrency: 10,
      enableAI: false,
      enableMonitoring: true,
      enableLogging: true,
      ...options
    };
    
    this.taskStore = new TaskStore();
    this.agentManager = new AgentManager();
    
    if (options.enableAI && options.userId && options.projectId) {
      this.agentAllocator = new SmartAgentAllocator({
        enableMemory: true,
        userId: options.userId,
        projectId: options.projectId,
      });
    }
    
    if (this.options.enableMonitoring) {
      this.monitor = new WorkflowMonitor(this.options.monitorOptions);
      this.setupMonitoringListeners();
    }
    
    if (this.options.enableLogging) {
      this.logger = new WorkflowLogger(this.options.loggerOptions);
    }
  }
  
  /**
   * 设置监控事件监听器
   */
  private setupMonitoringListeners(): void {
    if (!this.monitor) return;
    
    // 监听工作流事件
    this.on('workflow:start', ({ workflowId, executionId }) => {
      this.monitor!.startMonitoring(executionId, workflowId);
    });
    
    this.on('workflow:complete', (result) => {
      this.monitor!.completeMonitoring(result.executionId, result);
    });
    
    // 监听节点事件
    this.on('node:start', ({ nodeId, node, context }) => {
      this.monitor!.recordNodeStart(context.executionId, nodeId, node);
    });
    
    this.on('node:complete', ({ nodeId, result, context }) => {
      this.monitor!.recordNodeComplete(context.executionId, nodeId, result);
    });
    
    this.on('node:error', ({ nodeId, error, context }) => {
      this.monitor!.recordNodeError(context.executionId, nodeId, error);
    });
  }
  
  /**
   * 执行工作流
   */
  async execute(
    workflow: WorkflowDefinitionV2, 
    inputs: Record<string, any> = {}
  ): Promise<WorkflowExecutionResult> {
    const executionId = `exec-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const startTime = new Date();
    
    // 初始化执行上下文
    const context: WorkflowExecutionContext = {
      workflowId: workflow.id,
      executionId,
      startTime,
      inputs,
      variables: { ...workflow.variables, ...inputs },
      nodes: {},
      currentNodes: [],
      tasks: {},
      environment: {
        user: this.options.userId,
        projectId: this.options.projectId,
        workingDir: process.cwd(),
      },
    };
    
    // 初始化节点状态
    workflow.nodes.forEach(node => {
      context.nodes[node.id] = { status: 'pending' };
    });
    
    this.runningWorkflows.set(executionId, context);
    
    // 发送开始事件
    this.emit('workflow:start', { workflowId: workflow.id, executionId });
    
    // 记录日志
    if (this.logger) {
      this.logger.workflowStart(executionId, workflow.id, inputs);
    }
    
    try {
      // 执行入口节点
      await this.executeNodes(workflow.entryNodes, workflow, context);
      
      // 计算输出
      const outputs = this.calculateOutputs(workflow, context);
      
      // 构建执行结果
      const result: WorkflowExecutionResult = {
        executionId,
        workflowId: workflow.id,
        status: 'success',
        startTime,
        endTime: new Date(),
        duration: Date.now() - startTime.getTime(),
        outputs,
        nodes: context.nodes,
        createdTasks: Object.keys(context.tasks),
      };
      
      // 发送完成事件
      this.emit('workflow:complete', result);
      
      // 记录完成日志
      if (this.logger) {
        this.logger.workflowComplete(executionId, workflow.id, result);
      }
      
      return result;
      
    } catch (error) {
      logger.error('Workflow execution failed:', error);
      
      const result: WorkflowExecutionResult = {
        executionId,
        workflowId: workflow.id,
        status: 'failure',
        startTime,
        endTime: new Date(),
        duration: Date.now() - startTime.getTime(),
        nodes: context.nodes,
        errors: [{
          nodeId: 'unknown',
          error: error.message,
          timestamp: new Date(),
        }],
      };
      
      // 发送错误事件
      this.emit('workflow:error', { ...result, error });
      
      // 记录错误日志
      if (this.logger) {
        this.logger.workflowError(executionId, workflow.id, error as Error);
      }
      
      return result;
      
    } finally {
      this.runningWorkflows.delete(executionId);
    }
  }
  
  /**
   * 执行节点列表
   */
  private async executeNodes(
    nodeIds: string[], 
    workflow: WorkflowDefinitionV2,
    context: WorkflowExecutionContext
  ): Promise<void> {
    // 并行执行所有节点
    await Promise.all(
      nodeIds.map(nodeId => this.executeNode(nodeId, workflow, context))
    );
  }
  
  /**
   * 执行单个节点
   */
  private async executeNode(
    nodeId: string,
    workflow: WorkflowDefinitionV2,
    context: WorkflowExecutionContext
  ): Promise<any> {
    const node = workflow.nodes.find(n => n.id === nodeId);
    if (!node) {
      throw new Error(`Node not found: ${nodeId}`);
    }
    
    // 检查节点是否已执行
    if (context.nodes[nodeId].status !== 'pending') {
      return context.nodes[nodeId].result;
    }
    
    // 更新节点状态
    context.nodes[nodeId] = {
      status: 'running',
      startTime: new Date(),
    };
    
    // 发送节点开始事件
    this.emit('node:start', { nodeId, node, context });
    
    // 记录节点开始日志
    if (this.logger) {
      this.logger.nodeExecute(context.executionId, nodeId, node);
    }
    
    try {
      let result: any;
      
      // 根据节点类型执行
      switch (node.type) {
        case 'task':
          result = await this.executeTaskNode(node, context);
          break;
          
        case 'condition':
          result = await this.executeConditionNode(node, workflow, context);
          break;
          
        case 'loop':
          result = await this.executeLoopNode(node, workflow, context);
          break;
          
        case 'parallel':
          result = await this.executeParallelNode(node, workflow, context);
          break;
          
        case 'action':
          result = await this.executeActionNode(node, context);
          break;
          
        case 'wait':
          result = await this.executeWaitNode(node, context);
          break;
          
        case 'workflow':
          result = await this.executeSubWorkflowNode(node, context);
          break;
          
        case 'approval':
          result = await this.executeApprovalNode(node, context);
          break;
          
        default:
          throw new Error(`Unknown node type: ${node.type}`);
      }
      
      // 更新节点状态
      context.nodes[nodeId] = {
        status: 'success',
        startTime: context.nodes[nodeId].startTime,
        endTime: new Date(),
        result,
      };
      
      // 发送节点完成事件
      this.emit('node:complete', { nodeId, node, result, context });
      
      // 记录节点完成日志
      if (this.logger) {
        this.logger.nodeComplete(context.executionId, nodeId, result);
      }
      
      // 执行后续节点
      if (node.onSuccess) {
        await this.executeNodes(node.onSuccess, workflow, context);
      }
      if (node.onComplete) {
        await this.executeNodes(node.onComplete, workflow, context);
      }
      
      return result;
      
    } catch (error) {
      logger.error(`Node execution failed: ${nodeId}`, error);
      
      // 更新节点状态
      context.nodes[nodeId] = {
        status: 'failure',
        startTime: context.nodes[nodeId].startTime,
        endTime: new Date(),
        error: error.message,
      };
      
      // 发送节点错误事件
      this.emit('node:error', { nodeId, node, error, context });
      
      // 记录节点错误日志
      if (this.logger) {
        this.logger.nodeError(context.executionId, nodeId, error as Error);
      }
      
      // 执行失败处理节点
      if (node.onFailure) {
        await this.executeNodes(node.onFailure, workflow, context);
      }
      if (node.onComplete) {
        await this.executeNodes(node.onComplete, workflow, context);
      }
      
      // 如果配置了重试
      if (node.retry && node.retry.attempts > 0) {
        // TODO: 实现重试逻辑
      }
      
      throw error;
    }
  }
  
  /**
   * 执行任务节点
   */
  private async executeTaskNode(
    node: WorkflowNode,
    context: WorkflowExecutionContext
  ): Promise<ExtendedTask> {
    if (!node.task) {
      throw new Error('Task node missing task definition');
    }
    
    // 创建任务
    const task: ExtendedTask = {
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: this.resolveExpression(node.task.content || node.name, context),
      status: 'todo',
      priority: node.task.priority || 'medium',
      ...node.task,
      metadata: {
        ...node.task.metadata,
        workflowId: context.workflowId,
        executionId: context.executionId,
        nodeId: node.id,
      },
    };
    
    // 智能分配 Agent
    if (this.agentAllocator && this.options.enableAI) {
      const allocation = await this.agentAllocator.allocateAgents(task);
      task.agentAssignments = {
        primary: allocation.primary,
        reviewers: allocation.reviewers,
        consultants: allocation.consultants,
      };
    }
    
    // 保存任务
    const createdTask = await this.taskStore.addTask(task);
    context.tasks[createdTask.id] = createdTask as ExtendedTask;
    
    return createdTask as ExtendedTask;
  }
  
  /**
   * 执行条件节点
   */
  private async executeConditionNode(
    node: WorkflowNode,
    workflow: WorkflowDefinitionV2,
    context: WorkflowExecutionContext
  ): Promise<boolean> {
    if (!node.condition) {
      throw new Error('Condition node missing condition');
    }
    
    const result = this.evaluateCondition(node.condition, context);
    
    // 执行对应分支
    if (node.branches) {
      const branchNodes = result ? node.branches.true : node.branches.false;
      if (branchNodes) {
        await this.executeNodes(branchNodes, workflow, context);
      }
    }
    
    return result;
  }
  
  /**
   * 执行循环节点
   */
  private async executeLoopNode(
    node: WorkflowNode,
    workflow: WorkflowDefinitionV2,
    context: WorkflowExecutionContext
  ): Promise<any[]> {
    if (!node.loop || !node.loopBody) {
      throw new Error('Loop node missing loop configuration');
    }
    
    const results: any[] = [];
    let iterations = 0;
    const maxIterations = node.loop.maxIterations || 1000;
    
    switch (node.loop.type) {
      case 'count':
        const count = node.loop.count || 0;
        for (let i = 0; i < count && i < maxIterations; i++) {
          context.variables[`${node.id}_index`] = i;
          await this.executeNodes(node.loopBody, workflow, context);
          iterations++;
        }
        break;
        
      case 'while':
        while (
          node.loop.condition && 
          this.evaluateCondition(node.loop.condition, context) &&
          iterations < maxIterations
        ) {
          await this.executeNodes(node.loopBody, workflow, context);
          iterations++;
        }
        break;
        
      case 'foreach':
        const items = this.resolveExpression(node.loop.items || '[]', context);
        const itemName = node.loop.itemName || 'item';
        
        for (let i = 0; i < items.length && i < maxIterations; i++) {
          context.variables[itemName] = items[i];
          context.variables[`${node.id}_index`] = i;
          await this.executeNodes(node.loopBody, workflow, context);
          iterations++;
        }
        break;
    }
    
    context.nodes[node.id].iterations = iterations;
    return results;
  }
  
  /**
   * 执行并行节点
   */
  private async executeParallelNode(
    node: WorkflowNode,
    workflow: WorkflowDefinitionV2,
    context: WorkflowExecutionContext
  ): Promise<any[]> {
    if (!node.parallel || !node.parallel.branches) {
      throw new Error('Parallel node missing parallel configuration');
    }
    
    const results = await Promise.all(
      node.parallel.branches.map(async (branch, index) => {
        try {
          await this.executeNodes(branch, workflow, context);
          return { branch: index, status: 'success' };
        } catch (error) {
          if (node.parallel?.waitAll) {
            throw error;
          }
          return { branch: index, status: 'failure', error: error.message };
        }
      })
    );
    
    return results;
  }
  
  /**
   * 执行动作节点
   */
  private async executeActionNode(
    node: WorkflowNode,
    context: WorkflowExecutionContext
  ): Promise<any> {
    if (!node.action) {
      throw new Error('Action node missing action configuration');
    }
    
    const action = node.action;
    
    switch (action.type) {
      case 'command':
        return await this.executeCommand(action, context);
        
      case 'api':
        return await this.executeAPICall(action, context);
        
      case 'script':
        return await this.executeScript(action, context);
        
      case 'agent':
        return await this.executeAgent(action, context);
        
      default:
        throw new Error(`Unknown action type: ${action.type}`);
    }
  }
  
  /**
   * 执行命令
   */
  private async executeCommand(
    action: ActionConfig,
    context: WorkflowExecutionContext
  ): Promise<any> {
    if (!action.command) {
      throw new Error('Command action missing command');
    }
    
    const command = this.resolveExpression(action.command, context);
    const args = action.args?.map(arg => this.resolveExpression(arg, context)) || [];
    
    const { stdout, stderr } = await execAsync(`${command} ${args.join(' ')}`);
    
    if (stderr) {
      logger.warn(`Command stderr: ${stderr}`);
    }
    
    return { stdout, stderr };
  }
  
  /**
   * 执行 API 调用
   */
  private async executeAPICall(
    action: ActionConfig,
    context: WorkflowExecutionContext
  ): Promise<any> {
    if (!action.url) {
      throw new Error('API action missing URL');
    }
    
    const url = this.resolveExpression(action.url, context);
    const method = action.method || 'GET';
    const headers = action.headers || {};
    const body = action.body;
    
    const response = await axios({
      url,
      method,
      headers,
      data: body,
    });
    
    return response.data;
  }
  
  /**
   * 执行脚本
   */
  private async executeScript(
    action: ActionConfig,
    context: WorkflowExecutionContext
  ): Promise<any> {
    if (!action.script) {
      throw new Error('Script action missing script');
    }
    
    const script = this.resolveExpression(action.script, context);
    
    if (action.language === 'javascript') {
      const sandbox = {
        context,
        console,
        require,
      };
      
      const vmContext = vm.createContext(sandbox);
      const result = vm.runInContext(script, vmContext);
      
      return result;
    } else {
      // 其他语言通过命令行执行
      // TODO: 实现 Python、Bash 等脚本执行
      throw new Error(`Unsupported script language: ${action.language}`);
    }
  }
  
  /**
   * 执行 Agent
   */
  private async executeAgent(
    action: ActionConfig,
    context: WorkflowExecutionContext
  ): Promise<any> {
    if (!action.agentId || !action.prompt) {
      throw new Error('Agent action missing agentId or prompt');
    }
    
    await this.agentManager.initialize();
    const agent = this.agentManager.getAgent(action.agentId);
    
    if (!agent) {
      throw new Error(`Agent not found: ${action.agentId}`);
    }
    
    const prompt = this.resolveExpression(action.prompt, context);
    const result = await this.agentManager.executeAgent(agent, {
      task: { content: prompt },
      context: context.variables,
    });
    
    return result;
  }
  
  /**
   * 执行等待节点
   */
  private async executeWaitNode(
    node: WorkflowNode,
    context: WorkflowExecutionContext
  ): Promise<void> {
    if (!node.wait) {
      throw new Error('Wait node missing wait configuration');
    }
    
    if (node.wait.duration) {
      // 等待指定时长
      await new Promise(resolve => setTimeout(resolve, node.wait!.duration));
    } else if (node.wait.until) {
      // 等待条件满足
      const timeout = node.wait.timeout || 60000; // 默认60秒超时
      const startTime = Date.now();
      
      while (!this.evaluateCondition(node.wait.until, context)) {
        if (Date.now() - startTime > timeout) {
          throw new Error('Wait timeout');
        }
        await new Promise(resolve => setTimeout(resolve, 1000)); // 每秒检查一次
      }
    }
  }
  
  /**
   * 执行子工作流节点
   */
  private async executeSubWorkflowNode(
    node: WorkflowNode,
    context: WorkflowExecutionContext
  ): Promise<any> {
    // TODO: 实现子工作流执行
    throw new Error('Subworkflow execution not implemented yet');
  }
  
  /**
   * 执行审批节点
   */
  private async executeApprovalNode(
    node: WorkflowNode,
    context: WorkflowExecutionContext
  ): Promise<any> {
    // TODO: 实现审批逻辑
    throw new Error('Approval node not implemented yet');
  }
  
  /**
   * 评估条件
   */
  private evaluateCondition(
    condition: WorkflowCondition,
    context: WorkflowExecutionContext
  ): boolean {
    // 处理逻辑组合
    if (condition.and) {
      return condition.and.every(c => this.evaluateCondition(c, context));
    }
    
    if (condition.or) {
      return condition.or.some(c => this.evaluateCondition(c, context));
    }
    
    // 获取字段值
    const fieldValue = this.resolveExpression(condition.field, context);
    const compareValue = condition.value;
    
    // 评估操作符
    switch (condition.operator) {
      case 'equals':
        return fieldValue === compareValue;
      case 'not_equals':
        return fieldValue !== compareValue;
      case 'contains':
        return String(fieldValue).includes(String(compareValue));
      case 'not_contains':
        return !String(fieldValue).includes(String(compareValue));
      case 'starts_with':
        return String(fieldValue).startsWith(String(compareValue));
      case 'ends_with':
        return String(fieldValue).endsWith(String(compareValue));
      case 'greater_than':
        return Number(fieldValue) > Number(compareValue);
      case 'less_than':
        return Number(fieldValue) < Number(compareValue);
      case 'in':
        return Array.isArray(compareValue) && compareValue.includes(fieldValue);
      case 'not_in':
        return Array.isArray(compareValue) && !compareValue.includes(fieldValue);
      case 'exists':
        return fieldValue !== undefined && fieldValue !== null;
      case 'not_exists':
        return fieldValue === undefined || fieldValue === null;
      case 'regex':
        return new RegExp(String(compareValue)).test(String(fieldValue));
      default:
        throw new Error(`Unknown operator: ${condition.operator}`);
    }
  }
  
  /**
   * 解析表达式
   */
  private resolveExpression(expression: string, context: WorkflowExecutionContext): any {
    if (typeof expression !== 'string') {
      return expression;
    }
    
    // 替换变量引用 ${variable}
    return expression.replace(/\$\{([^}]+)\}/g, (match, path) => {
      const value = this.getValueByPath(context, path);
      return value !== undefined ? value : match;
    });
  }
  
  /**
   * 通过路径获取值
   */
  private getValueByPath(obj: any, path: string): any {
    const parts = path.split('.');
    let current = obj;
    
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return undefined;
      }
    }
    
    return current;
  }
  
  /**
   * 计算输出
   */
  private calculateOutputs(
    workflow: WorkflowDefinitionV2,
    context: WorkflowExecutionContext
  ): Record<string, any> {
    const outputs: Record<string, any> = {};
    
    if (workflow.outputs) {
      Object.entries(workflow.outputs).forEach(([key, output]) => {
        outputs[key] = this.resolveExpression(output.value, context);
      });
    }
    
    return outputs;
  }
  
  /**
   * 停止工作流
   */
  async stop(executionId: string): Promise<void> {
    const context = this.runningWorkflows.get(executionId);
    if (!context) {
      throw new Error(`Workflow execution not found: ${executionId}`);
    }
    
    // TODO: 实现停止逻辑
    this.emit('workflow:cancelled', { executionId });
  }
  
  /**
   * 获取运行中的工作流
   */
  getRunningWorkflows(): string[] {
    return Array.from(this.runningWorkflows.keys());
  }
}